// Every word the partner portal shows, in English. The other languages are the same
// shape (Dict), so a missing or misspelt key fails the build. Strings that hold
// markup (<strong>, <em>, <a>, <span>) are rendered with [innerHTML]; they are ours,
// never user input. Functions take counts and names, so each language can do its
// own plurals and word order.

export const en = {
  meta: {
    language: 'Language',
  },

  titles: {
    home: 'AudaTours Partners | Work with us',
    hotels: 'Hotels, Hostels & Cafés | AudaTours Partners',
    resellers: 'Resellers | AudaTours Partners',
    creators: 'Creators & Affiliates | AudaTours Partners',
    custom: 'Custom Tours & Ideas | AudaTours Partners',
    posters: 'Poster Studio | AudaTours Partners',
    join: 'Join | AudaTours Partners',
    login: 'Sign In | AudaTours Partners',
    dashboard: 'Dashboard | AudaTours Partners',
    terms: 'Partner Terms | AudaTours Partners',
  },

  nav: {
    partners: 'Partners',
    brandSub: 'Tourist Partners',
    findTour: 'Find a Tour',
    hotels: 'Hotels & Cafés',
    resellers: 'Resellers',
    groups: 'Groups',
    creators: 'Creators',
    custom: 'Something Else',
    posterStudio: 'Poster Studio',
    dashboard: 'Dashboard',
    signIn: 'Sign In',
    footerTitle: 'AudaTours Partners',
    footerBlurb: 'Self-guided audio walks with a story at every stop, in 1,000+ cities. Work with us the way that suits you.',
    partnerTerms: 'Partner Terms',
  },

  hub: {
    eyebrow: 'AudaTours Partners',
    title: 'Let’s work',
    titleAccent: 'together.',
    lede: 'Self-guided audio walks with a story at every stop, in 1,000+ cities. Hotels, resellers, groups, creators, councils: there’s a way in for everyone. Pick yours.',
    cardsLabel: 'Ways to work with us',
    cards: {
      hotels: { title: 'Hotels, hostels & cafés', body: 'Pin a free poster on your wall. Guests walk the city in their own language, and you keep 30% of what they buy.', tag: '30% commission', cta: 'Make a Poster' },
      resellers: { title: 'Resellers', body: 'Sell our tours to your customers and earn a commission on every one. Connect directly or through Bókun.', tag: 'Commission on every sale', cta: 'Become a Reseller' },
      groups: { title: 'Groups', body: 'A code for everyone in your group. Conferences, schools and universities, tour operators.', tag: 'From $5 a person', cta: 'See Group Prices' },
      creators: { title: 'Creators & affiliates', body: 'Post about us, share your link and earn 30% of what your audience buys. Start with a free month of Unlimited.', tag: 'Free month of Unlimited', cta: 'Start Earning' },
      custom: { title: 'Something else in mind?', body: 'We’re open to anything. A custom tour, a free tour for your festival, a tour that puts your town on the map.', tag: 'Open to anything', cta: 'Tell Us Your Idea' },
    },
    notSure: 'Not sure which fits? Email <a href="mailto:kes@audatours.com">kes@audatours.com</a> and we’ll work it out together.',
  },

  types: {
    resellers: {
      ticker: [
        { value: '1,500+', label: 'of our tours on Trip.com' },
        { value: '25%', label: 'commission through Bókun' },
        { value: '1,000+', label: 'cities' },
        { value: '50+', label: 'languages' },
      ],
      proofEyebrow: 'Already selling',
      proofTitle: 'You’ll find us',
      proofTitleAccent: 'here too',
      channels: [
        'Our biggest shelf: 1,500+ of our tours listed.',
        'The experiences marketplace for Germany and Austria.',
        'Where resellers connect to us, at 25% on every sale.',
      ],
      eyebrow: 'Resellers',
      title: 'Sell our tours.',
      titleAccent: 'Earn on every one.',
      lede: 'Travel agencies, booking sites, apps and concierge desks: add self-guided walks in 1,000+ cities to what you sell, with a commission on every sale.',
      cta: 'Get in Touch',
      secondary: '',
      trust: ['Commission on every sale', 'Direct or through Bókun', 'No set-up fees'],
      offersEyebrow: 'What you get',
      offersTitle: 'Pick how you',
      offersTitleAccent: 'connect',
      offers: [
        { title: 'Through Bókun', body: 'Already on Bókun? Find AudaTours in the marketplace and send us a proposal. Nothing to build.' },
        { title: 'Direct integration', body: 'Selling at volume? We can connect to your booking system, so every sale sends the buyer their tour.' },
        { title: 'What you sell', body: 'Audio walks with a story at every stop. Buyers play them on their phone, at their own pace.' },
      ],
      howTitle: 'Three steps.',
      howTitleAccent: 'Then you’re selling.',
      steps: [
        { title: 'Say hello', body: 'Tell us who you sell to, and where.' },
        { title: 'Agree terms', body: 'We agree a set price for each tour, and the commission you keep from it.' },
        { title: 'Start selling', body: 'List the tours. Each buyer gets a code that unlocks their tour in the AudaTours app.' },
      ],
      faqs: [
        { q: 'How much commission do you pay?', a: 'On Bókun it’s 25% of the price. For direct deals it depends on volume, so tell us what you have in mind.' },
        { q: 'Which tours can I sell?', a: 'Any of them: every tour in every city. Or only the cities your customers go to.' },
        { q: 'How do buyers get their tour?', a: 'They get a code with their booking. They type it into the AudaTours app and the tour is theirs.' },
        { q: 'Who sets the price?', a: 'We agree a set price for each tour up front, and your commission comes out of it.' },
      ],
      finalTitle: 'Let’s',
      finalTitleAccent: 'talk',
      finalTitleEnd: '.',
      finalBody: 'Tell us who you sell to, and we’ll come back with an offer.',
      finalCta: 'Email Us',
      mailSubject: 'Reseller enquiry',
      mailLines: ['Company', 'Website', 'Where you sell (countries or cities)', 'How you’d like to connect (Bókun or direct)'],
    },
    creators: {
      ticker: [
        { value: '30%', label: 'of every sale' },
        { value: '$15', label: 'max per Unlimited pass' },
        { value: '14 days', label: 'to buy after a click' },
        { value: '1 month', label: 'of Unlimited, free' },
      ],
      proofEyebrow: 'Creators like you',
      proofTitle: 'What our affiliates',
      proofTitleAccent: 'say',
      eyebrow: 'Creators & affiliates',
      title: 'Share the walk.',
      titleAccent: 'Keep 30%.',
      lede: 'Bloggers, YouTubers, Instagrammers, guides and history societies: share your link and earn 30% of every tour and Unlimited pass your audience buys on audatours.com.',
      cta: 'Join Free',
      secondary: 'Ask for a Free Month',
      trust: ['Free to join', '30% on tours and Unlimited', 'Paid by Stripe, every Friday'],
      offersEyebrow: 'What you get',
      offersTitle: 'More than',
      offersTitleAccent: 'a link',
      offers: [
        { title: 'A free month of Unlimited', body: 'Every tour in every city for a month, so you can walk a few before you talk about them. One per creator.' },
        { title: 'Your own link', body: 'One link for your bio, posts, videos and newsletter. It opens audatours.com and credits you for 14 days.' },
        { title: '30% of every sale', body: 'Of every tour, and of the first payment for Unlimited: up to $15 on a yearly pass.' },
        { title: 'Ready-made creatives', body: 'Posts, stories and posters with your link on them, in 11 languages. Download them from your dashboard.' },
      ],
      howTitle: 'Three steps.',
      howTitleAccent: 'About five minutes.',
      steps: [
        { title: 'Join free', body: 'Make a partner account. Your link is waiting in your dashboard.' },
        { title: 'Walk a tour', body: 'Ask for your free month of Unlimited and try a few in your city.' },
        { title: 'Share it', body: 'Post, film, write. Every sale through your link pays you 30%.' },
      ],
      faqs: [
        { q: 'Who can get the free month?', a: 'Anyone who makes things people read or watch: a blog, a channel, a newsletter, an account people follow. Email us your links and we’ll send you a code. One per person.' },
        { q: 'Do app purchases count?', a: 'No, only purchases on audatours.com. If someone downloads the app and buys in the App Store or Google Play, that sale doesn’t earn commission, so share your link, not the app store.' },
        { q: 'How long does my link credit me?', a: 'For 14 days after someone first opens it. If they’re new to AudaTours and buy in that time, you earn 30%.' },
        { q: 'How do I get paid?', a: 'Through Stripe. Connect your bank once in your dashboard and payouts go out every Friday.' },
      ],
      finalTitle: 'Your link is',
      finalTitleAccent: 'one minute',
      finalTitleEnd: ' away.',
      finalBody: 'Join free, grab your link, and ask for your free month.',
      finalCta: 'Join Free',
      mailSubject: 'Free Unlimited month for creators',
      mailLines: ['Your name', 'Where you post (links)', 'How many people follow you', 'Cities you’d like to walk'],
    },
    custom: {
      ticker: [
        { value: '1,000+', label: 'cities with tours' },
        { value: '2,800+', label: 'tours to start from' },
        { value: '50+', label: 'languages' },
        { value: '0', label: 'guides or groups to book' },
      ],
      hodStat: { value: '1,300+', label: 'free tours claimed at Heritage Open Days' },
      proofEyebrow: 'Done before',
      proofTitle: 'A couple we’re',
      proofTitleAccent: 'proud of',
      cases: [
        { eyebrow: 'Custom tour', title: 'Northampton’s shoe trade', meta: '12 stops', body: 'Northampton’s boots once walked the world. We made a 12-stop walk through the parlours, workshops and factory floors behind them: Clicking, Closing and the Sound of the Town.', cta: 'Hear the Tour' },
        { eyebrow: 'Free for a festival', title: 'Heritage Open Days', meta: '10 days', body: 'For England’s biggest heritage festival, every one of our tours in England was free for ten days. 1,300+ free tours were claimed.', cta: 'See the Festival Page' },
      ],
      eyebrow: 'Something else',
      title: 'Got an idea?',
      titleAccent: 'We’re all ears.',
      lede: 'Tourism boards, councils, festivals, museums, heritage sites, companies: we’re open to anything. Tell us what you’re thinking and we’ll work out how to make it happen.',
      cta: 'Tell Us Your Idea',
      secondary: '',
      trust: ['Open to anything', 'Tours in 1,000+ cities already', 'A real person replies'],
      offersEyebrow: 'For example',
      offersTitle: 'Things we',
      offersTitleAccent: 'can do',
      offers: [
        { title: 'A custom tour', body: 'A tour made for you: your route, your stories, your stops. Priced by the job.' },
        { title: 'A free tour for a while', body: 'Make our tours free for your festival, open day or anniversary. For Heritage Open Days, every tour in England was free for ten days.' },
        { title: 'A tour that promotes something', body: 'Put your town, venue or cause on the map with a tour that walks people to your door.' },
        { title: 'Something we haven’t listed', body: 'Not here? Ask anyway. If it gets people walking, we’re interested.' },
      ],
      howTitle: 'Three steps.',
      howTitleAccent: 'Your idea, walking.',
      steps: [
        { title: 'Tell us', body: 'What you want, where, and roughly when.' },
        { title: 'We shape it', body: 'We come back with how we’d do it and what it costs.' },
        { title: 'It goes live', body: 'Your tour goes up in the AudaTours app, ready for people to walk.' },
      ],
      faqs: [
        { q: 'Do custom tours cost money?', a: 'Usually, yes. We price each one by the job: how many stops, how much research, how many languages. Ask and we’ll give you a number.' },
        { q: 'Can you make an existing tour free?', a: 'Yes. We can open one or more of our tours for free for a set time, for a festival, event or campaign, then switch them back.' },
        { q: 'How long does it take?', a: 'A free window on an existing tour can be ready in days. A new custom tour takes longer, depending on the stops. Tell us your date and we’ll say if it works.' },
      ],
      finalTitle: 'Tell us what you’re',
      finalTitleAccent: 'thinking',
      finalTitleEnd: '.',
      finalBody: 'A few lines is plenty. We’ll take it from there.',
      finalCta: 'Email Us',
      mailSubject: 'Partnership idea',
      mailLines: ['Organisation', 'Your idea', 'Where (city or place)', 'When'],
    },
  },

  proof: {
    roles: {
      instagrammer: 'Travel Instagrammer',
      brand: 'Travel brand',
      hostel: 'Hostel in Tbilisi, Georgia',
    },
    viewContent: 'View Content',
    viewWebsite: 'View Website',
  },

  home: {
    quoteEyebrow: 'From a partner',
    quoteTitle: 'Straight from',
    quoteTitleAccent: 'a hostel',
    eyebrow: 'AudaTours Partners',
    title: 'Something to do for every guest.',
    titleAccent: 'In their own language.',
    lede: 'Print a free poster for reception. Guests scan it and walk the city with an audio guide on their phone, narrated in their own language, from a choice of 50. They start whenever they like, and you keep 30% of every tour and Unlimited pass they buy.',
    ctaPrimary: 'Make Poster',
    ctaSecondary: 'Become Partner',
    trustNoAccount: 'Free to print, no sign-up',
    trustStripe: '30% to you, paid every Friday',
    trustCities: '1,000+ cities',
    claimNudge: (n: number) => n === 1
      ? 'You’ve printed 1 poster. Join free to start earning from it →'
      : `You’ve printed ${n} posters. Join free to start earning from them →`,
    sampleBusiness: 'The Kingfisher Hostel',

    tickerLanguages: 'languages to listen in',
    tickerTours: 'of every tour, to you',
    tickerCities: 'cities with tours',
    tickerCitiesCount: '1,000+',
    tickerFree: 'Free',
    tickerFreeSub: 'to join, ever',

    howEyebrow: 'How it works',
    howTitle: 'Four steps.',
    howTitleAccent: 'About four minutes.',
    steps: [
      { title: 'Type your city', body: 'Every city with tours is in there. The QR code opens that city’s page — all its tours, ready to play.' },
      { title: 'Print it', body: 'A4, A5 or A6 on any printer. No account, no waiting. Add your name if you like.' },
      { title: 'Pin it up', body: 'Reception, the lift, the breakfast table, the window. Anywhere a guest asks what to do today.' },
      { title: 'Get 30%', body: 'Make a free account and every sale through your poster pays you 30%, straight to your bank via Stripe.' },
    ],

    sizesEyebrow: 'Three sizes',
    sizesTitle: 'Big on the wall.',
    sizesTitleAccent: 'Small on the table.',
    openStudio: 'Open the Poster Studio',
    onePerSheet: 'one per sheet',
    perSheet: (n: number) => `${n} per A4 sheet`,

    earnEyebrow: 'What you earn',
    earnTitle: 'Same deal for everyone.',
    earnTitleAccent: '30%.',
    earnBody: 'No tiers, no targets, no minimum. A guest scans your poster, buys a tour of the city or an Unlimited pass, and 30% of what they paid is yours.',
    earnTours: '<strong>Tours</strong> — 30% of every tour bought through your poster.',
    earnUnlimited: '<strong>Unlimited</strong> — 30% of the first payment, up to $15 on a yearly pass. Every tour, every city.',
    earnWindow: '<strong>14 days</strong> — buy any time in the fortnight after their first scan and it counts.',
    earnWeb: '<strong>On the web</strong> — purchases on audatours.com count. App Store and Google Play purchases don’t.',
    earnStripe: '<strong>Stripe</strong> — payouts every Friday, straight to your bank.',
    earnCardTitle: 'Your dashboard',
    earnScans: 'Scans',
    earnSales: 'Sales',
    earnEarned: 'Earned',
    earnNote: 'Illustrative. Watch scans and sales for every poster you print.',

    whoEyebrow: 'Made for',
    whoTitle: 'Anywhere travellers',
    whoTitleAccent: 'ask what to do',
    whoTitleEnd: '.',
    who: [
      { title: 'Hostels & hotels', body: '“What should we see?” gets asked at your desk fifty times a week, and not always in English. Point at the wall.' },
      { title: 'Cafés, bars & shops', body: 'A small A6 on every table. Guests plan tomorrow over coffee and you earn from it.' },
      { title: 'Guides & creators', body: 'Share your link in your bio, newsletter or blog. Same 30%, no poster needed.' },
    ],

    faqEyebrow: 'Questions',
    faqTitle: 'Good to',
    faqTitleAccent: 'know',
    faqTitleEnd: '.',
    faqMore: 'Something else? Email <a href="mailto:help@audatours.com">help@audatours.com</a>.',
    faqs: [
      { q: 'What do my guests get?', a: 'An audio walking tour of your city on their own phone, narrated in their own language, from a choice of 50. They pick a tour, put their headphones in and start whenever they like.' },
      { q: 'What does it cost them?', a: 'A few dollars a tour, paid on their own phone, or one Unlimited pass for every tour in every city. You don’t sell or handle anything at the desk.' },
      { q: 'Do I need an account to print a poster?', a: 'No. Pick a city, print, done. Make a free account afterwards on the same browser and the posters you printed are linked to you. Commission starts when you join: sales before that don’t earn any.' },
      { q: 'How much do I earn?', a: '30% of every tour and every Unlimited pass bought through your poster or link. A yearly Unlimited pass earns you up to $15. Same rate for everyone, from your first sale.' },
      { q: 'Which sales count?', a: 'Purchases on audatours.com by new visitors who scan your poster or open your link, within 14 days of their first visit. Tours count every time; for Unlimited, it’s the first payment.' },
      { q: 'Do app purchases count?', a: 'No. Commission is paid on web purchases only, on audatours.com. If someone downloads the app and buys in the App Store or Google Play, that sale doesn’t earn commission.' },
      { q: 'What if a purchase is refunded?', a: 'The 30% on it comes back off your earnings. If it was already sent, it’s reversed from your Stripe account.' },
      { q: 'How do I get paid?', a: 'Through Stripe. Connect your bank once and your 30% is sent automatically, with payouts every Friday. Anything you earn before you connect is held for you and sent once you do.' },
      { q: 'Where does the QR code go?', a: 'Straight to the city page on audatours.com, with every tour in that city, in the poster’s language. Each poster gets its own short link, audatours.com/go/…, so you can see which poster brings people in.' },
      { q: 'Can I print posters in other languages?', a: 'Yes. Posters print in English unless you pick another language in the Poster Studio. The QR code then opens the city page in that language too.' },
      { q: 'Can I make posters for more than one city?', a: 'As many as you like. Every city with tours is in the Poster Studio, and each poster tracks its own scans and sales.' },
      { q: 'How should I print it?', a: 'Any home or office printer is fine. Print at 100% (“Actual size”). A5 and A6 come two and four to an A4 sheet, with dotted lines to cut along.' },
    ],

    finalTitle: 'Your first poster is',
    finalTitleAccent: 'one minute',
    finalTitleEnd: 'away.',
    finalBody: 'Type your city. Hit print. Pin it up.',
  },

  sizes: {
    a4: { blurb: 'One big poster. Reception walls and windows.' },
    a5: { blurb: 'Two per sheet. Noticeboards and lifts.' },
    a6: { blurb: 'Four per sheet. Tables, desks and room cards.' },
  },

  designs: {
    olive: { label: 'Olive', note: 'Bold and dark' },
    lime: { label: 'Lime', note: 'Bright and loud' },
    paper: { label: 'Paper', note: 'Easy on ink' },
    photo: { label: 'Photo', note: 'Your city, full colour' },
  },

  studio: {
    eyebrow: 'Poster Studio',
    title: 'Make your',
    titleAccent: 'poster',
    intro: 'Pick a city, a size and a look. Print it now — no account needed.',
    unclaimedTitle: (n: number) => n === 1 ? '1 poster isn’t earning yet.' : `${n} posters aren’t earning yet.`,
    unclaimedBody: 'Join free and they’re yours, 30% and all.',

    stepCity: 'Which city?',
    cityPlaceholder: 'Type a city, e.g. Lisbon',
    tours: (n: number) => n === 1 ? '1 tour' : `${n} tours`,
    noCity: (query: string) => `No tours in “${query}” yet. Try the nearest big city.`,
    destination: (url: string, tours: number) => tours === 1
      ? `Scans open <a href="https://${url}" target="_blank" rel="noopener"><strong>${url}</strong></a> — 1 tour ready to go.`
      : `Scans open <a href="https://${url}" target="_blank" rel="noopener"><strong>${url}</strong></a> — ${tours} tours ready to go.`,

    stepLanguage: 'Poster language',
    languageHint: 'The poster’s words, and the language of the page its QR code opens.',

    stepSize: 'What size?',
    onePerSheet: '1 per sheet',
    perSheet: (n: number) => `${n} per A4 sheet`,

    stepLook: 'Pick a look',

    stepName: 'Your name on it',
    optional: 'optional',
    namePlaceholder: 'e.g. The Kingfisher Hostel',

    pickCityFirst: 'Type your city and pick it from the list first.',
    preparing: 'Getting it ready…',
    printA4: 'Print A4 poster',
    printMany: (n: number, size: string) => `Print ${n} ${size} posters`,
    linkedTo: (name: string) => `Linked to <strong>${name}</strong>. Every sale through it earns you 30%.`,
    freeNote: 'Free, no sign-up. Print at 100% (“Actual size”). Join afterwards and the poster’s linked to you.',

    stageA4: 'A4 poster',
    stageMany: (n: number, size: string) => `${n} × ${size} on one A4 sheet`,
    portrait: 'portrait',
    landscape: 'landscape',
    stageNote: 'Your poster gets its own code when you print. It opens your city’s tours and credits you.',
    printHint: 'Use the Print button in the Poster Studio: it gives your poster its own QR code.',

    claimTitle: 'Nice. Now get',
    claimTitleAccent: 'paid',
    claimTitleEnd: 'for it.',
    claimIntro: 'Make a free partner account and that poster’s linked to you:',
    claimPerkMoney: '<strong>30%</strong> of every tour and Unlimited pass bought through it on audatours.com',
    claimPerkStats: 'See scans and sales for each poster',
    claimPerkStripe: 'Paid out through Stripe, every Friday',
    claimSmall: 'Earnings start when you create your account. Join now so you don’t miss a sale.',
    claimJoin: 'Create My Free Account',
    claimHaveAccount: 'I Have an Account',
    claimLater: 'Not now — keep printing',

    toastPrinted: 'Printed. It’s in your dashboard, earning 30%.',
    toastOpen: 'Open',
  },

  auth: {
    pitchEyebrow: 'AudaTours Partners',
    pitchLine1: 'Your wall.',
    pitchLine2: 'Their walk.',
    pitchLine3: 'Your 30%.',
    pitchPrint: 'Print posters for any city with tours',
    pitchScan: 'Guests scan and start walking',
    pitchMoney: '30% of every tour and Unlimited pass',
    pitchStripe: 'Paid out by Stripe, every Friday',
    email: 'Email',
    password: 'Password',
  },

  join: {
    eyebrow: 'Free partner account',
    title: 'Start earning',
    titleAccent: '30%',
    linked: (count: number, cities: string) => count === 1
      ? `Your poster for <strong>${cities}</strong> will be linked to this account.`
      : `Your ${count} posters for <strong>${cities}</strong> will be linked to this account.`,
    citiesTwo: (a: string, b: string) => `${a} and ${b}`,
    citiesMore: (a: string, b: string, more: number) => `${a}, ${b} and ${more} more`,
    intro: 'Takes a minute. Then every poster you print earns you money.',
    iAm: 'I’m a…',
    typeAccommodation: 'Hostel or hotel',
    typeBusiness: 'Café, bar or shop',
    typeIndividual: 'Guide or creator',
    name: 'Your name',
    business: 'Business name',
    passwordPlaceholder: '6+ characters',
    signedInAs: (email: string) => `Signed in as <strong>${email}</strong>.`,
    notYou: 'Not you?',
    terms: 'I have read and accept the <a href="/terms" target="_blank">Partner Terms</a>: 30% on tours and Unlimited bought on audatours.com (not in the app), paid through Stripe.',
    errorName: 'Tell us your name.',
    errorBusiness: 'Add your business name.',
    errorEmail: 'That email doesn’t look right.',
    errorPassword: 'Pick a password with at least 6 characters.',
    errorTerms: 'Tick the box to accept the partner terms.',
    busy: 'Setting you up…',
    submit: 'Create My Account',
    already: 'Already a partner?',
    signIn: 'Sign in',
  },

  login: {
    eyebrow: 'Welcome back',
    title: 'Sign in',
    intro: 'See your posters, scans and earnings.',
    busy: 'Signing in…',
    submit: 'Sign In',
    forgot: 'Forgot your password?',
    newHere: 'New here? <a href="/join">Create a free account</a> or <a href="/posters">print a poster first</a>.',
    typeEmailFirst: 'Type your email above first.',
    resetSent: 'Check your inbox for a link to reset your password.',
  },

  dashboard: {
    creativesTitle: 'Creatives',
    creativesBody: 'Posts and stories with your link on them, in any of 11 languages. Download one, post it, earn 30%.',
    creativeSquare: 'Square post',
    creativePortrait: 'Portrait post',
    creativeStory: 'Story',
    creativeCity: 'City',
    creativeLanguage: 'Language',
    creativeLook: 'Look',
    creativeDownload: 'Download PNG',
    creativePreparing: 'Getting it ready…',
    creativeCaption: (city: string, url: string) => `${city}, by ear. Self-guided audio tours with a story at every stop: ${url}`,
    creativeCopyCaption: 'Copy Caption',
    eyebrow: 'Partner dashboard',
    greeting: (name: string) => `Hi ${name}.`,
    there: 'there',
    intro: 'Every poster below earns you 30% of what people buy through it on audatours.com.',
    newPoster: 'New Poster',
    signOut: 'Sign Out',

    welcomeTitle: 'You’re in.',
    welcomeClaimed: (n: number) => n === 1
      ? 'Your poster is now linked to you and earning 30%.'
      : `Your ${n} posters are now linked to you and earning 30%.`,
    welcomeFirst: 'Print your first poster and it starts earning straight away.',
    welcomeNext: 'Next: set up payouts so we can pay you.',
    dismiss: 'Dismiss',

    earned: 'Earned',
    earnedSub: '30% of every sale, all time',
    waiting: 'Waiting to pay out',
    waitingReady: 'Sending with the next payout',
    waitingNotReady: 'Paid once Stripe is set up',
    sales: 'Sales',
    salesSub: (tours: number, unlimited: number) => `${tours === 1 ? '1 tour' : `${tours} tours`} · ${unlimited} Unlimited`,
    scans: 'Scans',
    scansSub: (n: number) => `${n} in the last 14 days`,

    termsTitle: 'Our partner terms have changed',
    termsBody: 'You now earn a flat 30% on tours and Unlimited bought on audatours.com. App purchases don’t earn commission.',
    termsTick: 'I have read and accept the <a href="/terms" target="_blank">Partner Terms</a>.',
    termsAccept: 'Accept',
    termsError: 'That didn’t save. Try again in a moment.',

    payChecking: 'Checking payouts…',
    payCheckingSub: 'One moment.',
    payReady: 'Payouts are on',
    payReadySub: 'Stripe sends your 30% to your bank every Friday.',
    payError: 'We couldn’t check your payouts',
    payErrorSub: 'Stripe didn’t answer. Try again in a minute.',
    paySetup: 'Set up payouts',
    paySetupSub: 'Connect a Stripe account so we can send your 30%. It takes about five minutes.',
    payWaiting: (amount: string) => `${amount} is waiting for you.`,
    payRetry: 'Stripe needs a few more details. Pick up where you left off.',
    payStripeError: 'We couldn’t reach Stripe just now. Try again in a minute.',
    openStripe: 'Open Stripe',
    tryAgain: 'Try Again',
    finishSetup: 'Finish Setup',
    setUpPayouts: 'Set Up Payouts',

    postersTitle: 'Your posters',
    makeAnother: 'Make Another',
    noPosters: 'No posters yet',
    noPostersSub: 'Pick your city and print one in under a minute.',
    makePoster: 'Make a Poster',
    made: (date: string) => `Made ${date}`,
    posterScans: 'scans',
    posterSales: 'sales',
    posterEarned: 'earned',
    reprint: 'Reprint',
    copied: 'Copied',
    copyLink: 'Copy Link',
    copy: 'Copy',
    yourCity: 'Your City',

    chartTitle: 'Scans, last 14 days',
    chartTip: (date: string, n: number) => n === 1 ? `${date}: 1 scan` : `${date}: ${n} scans`,

    linkTitle: 'Your link',
    linkBody: 'Share it anywhere: your website, emails, Instagram bio. It opens audatours.com and credits you.',

    salesTitle: 'Sales',
    webOnly: 'Commission is paid on purchases made on audatours.com. Purchases in the AudaTours app (App Store or Google Play) don’t earn commission, and a refunded purchase takes its commission back. <a href="/terms">Partner Terms</a>',
    noSales: 'No sales yet. Once someone buys through one of your posters, it shows up here.',
    colDate: 'Date',
    colWhat: 'What',
    colFrom: 'From',
    colPrice: 'Price',
    colCommission: 'Your 30%',
    colStatus: 'Status',
    unlimited: 'Unlimited',
    tour: 'Tour',
    poster: 'Poster',
    yourLink: 'Your link',
    refunded: 'Refunded',
    paid: 'Paid',
    waitingPill: 'Waiting',
  },

  terms: {
    effective: 'Effective 20 October 2026',
    title: 'Partner terms',
    translationNote: '',
    intro: 'These terms cover the AudaTours Partner Programme: printing AudaTours posters, sharing your partner link, and the commission you earn from them. Creating a partner account means you accept them.',
    sections: [
      { h: '1. Who we are', p: ['The programme is run by Explorer Innovations Ltd, registered in England and Wales under company number 14639977, registered office 61 Bridge Street, Kington, England, HR5 3DJ. “We” and “us” mean that company; “you” means the partner.'] },
      { h: '2. Eligibility', p: ['You must be 18 or over and give us accurate details. A business partner must be authorised to act for that business. We may close an account that gives false information.'] },
      { h: '3. Posters and links', p: [
        'Anyone can print an AudaTours poster without an account. Each poster carries its own link (audatours.com/go/…) and QR code. When you create a partner account on the browser you printed from, those posters are linked to you. You can also share your partner link anywhere you like.',
        'Use the posters and links as we provide them, and don’t alter the QR codes or the AudaTours branding without our written consent. We may change where a link points, for example when a city page moves.',
      ] },
      { h: '4. Commission', p: [
        '<strong>30%</strong> of the price paid for any tour bought on audatours.com through your poster or link.',
        '<strong>30%</strong> of the first payment for an Unlimited subscription bought on audatours.com through your poster or link. Renewals are not included.',
        '<strong>Web purchases only.</strong> Commission is paid only on purchases made on the AudaTours website (audatours.com). Purchases made in the AudaTours app, through the Apple App Store or Google Play, do not earn commission, even when the buyer first found us through your poster or link.',
        'A sale counts when the buyer is new to AudaTours and buys within 14 days of first arriving through your poster or link. Commission is calculated on the price paid, in US dollars.',
        'Commission is earned only on sales made after you claim a poster. Sales made through a poster before you claim it earn no commission.',
        '<strong>Refunds.</strong> If we refund a purchase, the commission on it is taken back: reversed from your Stripe account if it has already been sent, or removed from the commission we are holding for you.',
      ] },
      { h: '5. Payment', p: ['We pay commission through Stripe. You need a Stripe account in good standing, connected from your dashboard. Commission is transferred to your Stripe account when it is earned, and Stripe pays it to your bank on its weekly schedule, usually Fridays. Commission earned before your Stripe account is ready is held and sent once it can receive payouts. You are responsible for any tax on what you earn.'] },
      { h: '6. Ending the partnership', p: ['You can stop at any time by emailing us. We may end your participation at any time, for example for misuse of the materials or fraudulent sales. Commission properly earned before the end date is still paid.'] },
      { h: '7. Brand and materials', p: ['The AudaTours name, logo, posters and QR codes belong to us. You get a limited, revocable licence to use them only to promote AudaTours under these terms.'] },
      { h: '8. Confidentiality', p: ['Keep confidential anything we share with you about the programme that isn’t public, including customer data, payment details and commercial terms, and don’t pass it to third parties.'] },
      { h: '9. Liability', p: ['We work hard to track every scan and sale, but we are not liable for commission lost to technical faults outside our reasonable control. Nothing in these terms limits liability that cannot be limited by law.'] },
      { h: '10. Changes', p: ['We may update these terms. We will tell you about material changes by email, and continuing in the programme after that means you accept them.'] },
      { h: '11. Law', p: ['These terms are governed by the laws of England and Wales, and the courts of England and Wales have exclusive jurisdiction.'] },
      { h: '12. Contact', p: ['Questions? Email <a href="mailto:help@audatours.com">help@audatours.com</a>.'] },
    ],
  },

  errors: {
    emailInUse: 'There’s already an account with that email. Sign in instead.',
    invalidEmail: 'That email doesn’t look right.',
    weakPassword: 'Pick a password with at least 6 characters.',
    wrongPassword: 'That email and password don’t match.',
    userNotFound: 'We couldn’t find an account with that email.',
    tooManyRequests: 'Too many tries. Give it a minute and try again.',
    offline: 'You look offline. Check your connection and try again.',
    generic: 'Something went wrong. Try again in a moment.',
  },
};

export type Dict = typeof en;
